import { Element, Parent } from 'hast';
import type { Plugin } from 'unified';
import { visit } from 'unist-util-visit';

import { type MethodType, MethodTypes, CalcMethod } from './CalcMethod';

function extractBody(table: Element): TableData {
  const tableData: TableData = [];

  visit(table, { type: 'element', tagName: 'tbody' }, (tbody) => {
    visit(tbody, { type: 'element', tagName: 'tr' }, (tr) => {
      const row: Array<any> = [];
      visit(tr, { type: 'element', tagName: 'td' }, (td) => {
        visit(td, 'text', (tdText) => {
          row.push(tdText.value);
        });
      });
      tableData.push(row);
    });
  });
  return tableData;
}

function getReplaceCellPositions(data: TableData): Array<{ row: number; column: number; methodType: MethodType }> {
  const replaceCellPositions = [];

  for (let row = 0; row < data.length; row++) {
    for (let column = 0; column < data[row].length; column++) {
      const value = data[row][column].trim();
      if (MethodTypes.includes(value)) {
        replaceCellPositions.push({ row, column, methodType: value });
      }
    }
  }

  return replaceCellPositions;
}

function handleCalcMethod(
    data: TableData,
    calcData: Array<{ row: number; column: number; methodType: MethodType }>,
): Array<{ row: number; column: number; calcResult?: number }> {
  const calculatedData: Array<{ row: number; column: number; calcResult?: number }> = [];
  calcData.forEach(({ row, column, methodType }) => {
    let calcResult;

    try {
      calcResult = CalcMethod[methodType](data, { row, column });
    }
    catch (err) {
      /*
       * mathjs は集計対象が空だと例外を投げる (sum を除く)。
       * 例えば数値を1つも含まない列に {vavg} を置いた場合がこれにあたる。
       *
       * ここで握らないと、例外は rehype プラグインの visit をそのまま抜けていく。
       * GROWI では markdown 本文全体が1つの ReactMarkdown なので、
       * 本文が丸ごと描画されなくなる。
       *
       * undefined にしておけば replaceCalculatedData が '!CalcErr!' を表示する。
       */
      calcResult = undefined;
    }

    calculatedData.push({ row, column, calcResult });
  });

  return calculatedData;
}

function replaceCalculatedData(table: Element, calculatedData: Array<{ row: number; column: number; calcResult?: number }>) {
  let row = 0;
  let col = 0;

  visit(table, { type: 'element', tagName: 'tbody' }, (tbody) => {
    row = 0;
    visit(tbody, { type: 'element', tagName: 'tr' }, (tr) => {
      col = 0;
      visit(tr, { type: 'element', tagName: 'td' }, (td) => {
        const d = calculatedData.find((it) => it.row === row && it.column === col);
        col++;
        if (d == null) return;

        visit(td, 'text', (tdText) => {
          tdText.value = d.calcResult?.toString() || '!CalcErr!';
        });
      });
      row++;
    });
  });
}

/**
 * unified plugin that calculate proprietary methods in `table` element
 *
 * @returns unified plugin
 */
export const calcTable: Plugin = () => {
  return (tree) => {
    visit(tree, { type: 'element', tagName: 'table' }, (node: Element, index, parent: Parent) => {
      if (parent == null || index == null) return;

      const body = extractBody(node);
      const replaceCellPositions = getReplaceCellPositions(body);
      const calculatedData = handleCalcMethod(body, replaceCellPositions);

      replaceCalculatedData(node, calculatedData);
    });
  };
};
